import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import type { CaptureContext, CaptureEmployeeLookup } from '@/shared/api/types';

// Alle Capture-Aufrufe: auth:false - der Token IST das Credential.

export function useCaptureContext(token: string) {
  return useQuery({
    queryKey: ['capture', token],
    queryFn: () => api<CaptureContext>(`/api/capture/${token}`, { auth: false }),
    retry: false, // 403/404 = Link ungueltig, kein Netzproblem
  });
}

/**
 * Mitarbeiter NACHSCHLAGEN statt Liste laden. Frueher holte diese Abfrage beim
 * blossen Seitenaufruf die vollstaendige Belegschaft - an jeden, der den Token
 * hat. Jetzt laeuft sie erst ab EMPLOYEE_LOOKUP_MIN Zeichen und nur auf
 * Eingabe (ADR-032).
 */
export const EMPLOYEE_LOOKUP_MIN = 2;

export function useCaptureEmployeeLookup(token: string, q: string) {
  const suche = q.trim();
  return useQuery({
    queryKey: ['capture-employees', token, suche],
    queryFn: () =>
      api<CaptureEmployeeLookup>(
        `/api/capture/${token}/employees?q=${encodeURIComponent(suche)}`,
        { auth: false },
      ),
    enabled: suche.length >= EMPLOYEE_LOOKUP_MIN,
  });
}

export interface SubmitReportInput {
  employeeId?: string;
  noteText?: string;
  workTimes?: { durationMinutes: number; breakMinutes?: number; note?: string }[];
  materials?: { name: string; quantity: number; unit: string }[];
  extraServices?: { title: string; reason?: string; category?: string }[];
}

export interface SubmitReportResponse {
  id: string;
  status: string;
  submittedAt: string;
  message: string;
}

export function useSubmitReport(token: string) {
  return useMutation({
    mutationFn: (input: SubmitReportInput) =>
      api<SubmitReportResponse>(`/api/capture/${token}/reports`, {
        method: 'POST',
        auth: false,
        body: input,
      }),
  });
}

interface PresignResponse {
  mediaId: string;
  uploadUrl: string;
  expiresInSeconds: number;
}

export type MediaPhase = 'BEFORE' | 'AFTER' | 'OTHER';

/**
 * Kompletter Upload: presign -> PUT (direkt zu MinIO) -> confirm.
 * onProgress liefert 0..100 fuer die Fortschrittsanzeige (Kap. 9:
 * "Upload-Fortschritt anzeigen"). XHR statt fetch, weil fetch keinen
 * Upload-Progress kann.
 */
export async function uploadMedia(
  token: string,
  reportId: string,
  file: File | Blob,
  type: 'PHOTO' | 'AUDIO',
  phase: MediaPhase,
  onProgress?: (pct: number) => void,
): Promise<{ id: string; size: number }> {
  const mimeType = file.type || (type === 'PHOTO' ? 'image/jpeg' : 'audio/webm');

  const presign = await api<PresignResponse>(
    `/api/capture/${token}/reports/${reportId}/media/presign`,
    { method: 'POST', auth: false, body: { type, phase, mimeType, size: file.size } },
  );

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presign.uploadUrl);
    xhr.setRequestHeader('Content-Type', mimeType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload fehlgeschlagen (${xhr.status})`));
    xhr.onerror = () => reject(new Error('Netzwerkfehler beim Upload.'));
    xhr.send(file);
  });

  return api<{ id: string; size: number }>(`/api/capture/${token}/media/${presign.mediaId}/confirm`, {
    method: 'POST',
    auth: false,
  });
}
